import { Checkbox } from '@/shared/ui/forms/inputs/checkbox'
import { Input } from '@/shared/ui/forms/inputs/input'
import { InputLabel } from '@/shared/ui/forms/inputs/inputLabel'
import { Select } from '@/shared/ui/forms/inputs/select'

import { type FC } from 'react'

import { useAPIErrorSimulator } from './useAPIErrorSimulator'

/**
 * APIErrorSimulator - UI panel for the Axios error interceptor.
 *
 * Provides:
 * - URL pattern input (intercepts requests whose URL contains the string)
 * - Error status code selector (dropdown of common HTTP codes)
 * - Enable/disable toggle
 *
 * State is stored in Jotai atoms so it persists across Dialog open/close cycles.
 * The actual Axios interceptor is managed by `useAPIErrorSimulatorEffect`, which
 * must be mounted OUTSIDE the Dialog (see `TestingPanel`).
 *
 * Supports both controlled mode (via `simulateError` + `onToggle` props) and
 * uncontrolled mode (reads/writes the global Jotai atoms).
 *
 * @param simulateError - Controlled mode: whether to simulate errors (optional)
 * @param onToggle - Controlled mode: callback when checkbox is toggled (optional)
 *
 * @example Uncontrolled (inside TestingPanel Dialog):
 * ```tsx
 * <APIErrorSimulator />
 * ```
 *
 * @example Controlled (in a story):
 * ```tsx
 * const [simulateError, setSimulateError] = useState(false)
 * <APIErrorSimulator simulateError={simulateError} onToggle={setSimulateError} />
 * ```
 */
export type APIErrorSimulatorProps = {
  simulateError?: boolean
  onToggle?: (checked: boolean) => void
}

export const APIErrorSimulator: FC<APIErrorSimulatorProps> = ({
  simulateError: controlledSimulateError,
  onToggle,
}) => {
  const {
    simulateError,
    urlPattern,
    errorStatus,
    errorCodeOptions,
    handleToggle,
    handleUrlPatternChange,
    handleStatusChange,
  } = useAPIErrorSimulator({
    simulateError: controlledSimulateError,
    onToggle,
  })

  return (
    <div className="flex flex-col gap-3">
      <Checkbox.Option checked={simulateError} onChange={handleToggle} size="md">
        Simulate API Error
      </Checkbox.Option>

      {simulateError && (
        <>
          <div className="flex flex-col gap-1">
            <InputLabel>
              <span className="text-xs font-medium text-gray-500">URL Pattern</span>
            </InputLabel>
            <Input
              type="text"
              value={urlPattern}
              onChange={handleUrlPatternChange}
              placeholder="e.g. /onboardings"
            />
            <p className="text-xs text-gray-400">
              Intercepts requests whose URL contains this string. Use <code>/</code> to match all.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <InputLabel>
              <span className="text-xs font-medium text-gray-500">Error Status Code</span>
            </InputLabel>
            <Select
              mode="single"
              options={errorCodeOptions}
              value={errorStatus}
              onChange={handleStatusChange}
              placeholder="Select a status code"
            />
          </div>
        </>
      )}
    </div>
  )
}
